'use client';

import { createHttpClient } from '@outpoll/http';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import type { FetchNewsParams } from './client';
import {
  fetchNewsList,
  fetchStrapiSectionsByCategory,
  fetchStrapiTopStoriesByCategory,
} from './client';
import {
  type SearchEventsProxyParams,
  type SearchEventsProxyResult,
  searchEventsProxy,
} from './eventsClient';
import { mapStrapiNews } from './mappers';
import type { NewsList, StrapiCategoryItem, StrapiNewsItem, StrapiNewsStoryItem } from './models';
import { type SearchNewsProxyResult, searchNewsProxy } from './newsSearchClient';
import { type SearchProfilesProxyResult, searchProfilesProxy } from './profilesClient';
import type { StrapiNewsResponseDto } from './types';
import { localeToStrapiLanguage, resolveStrapiBaseUrl } from './utils';

export const STRAPI_SECTIONS_QK = 'strapi-sections';
export const STRAPI_TOP_STORIES_QK = 'strapi-top-stories';
export const STRAPI_SEARCH_NEWS_QK = 'strapi-search-news';
export const STRAPI_SEARCH_NEWS_PROXY_QK = 'strapi-search-news-proxy';
export const STRAPI_HOTTEST_NEWS_QK = 'strapi-hottest-news';
export const STRAPI_SEARCH_EVENTS_QK = 'strapi-search-events';
export const STRAPI_TRENDING_EVENTS_QK = 'strapi-trending-events';
export const STRAPI_SEARCH_PROFILES_QK = 'strapi-search-profiles';
export const STRAPI_TRENDING_PROFILES_QK = 'strapi-trending-profiles';
export const STRAPI_TRENDING_NEWS_PROXY_QK = 'strapi-trending-news-proxy';
export const STRAPI_SEARCH_NEWS_TITLES_QK = 'strapi-search-news-titles';
export const STRAPI_LATEST_NEWS_QK = 'strapi-latest-news';

const MIN_SEARCH_LENGTH = 2;

const BASE_URL = resolveStrapiBaseUrl();
const http = createHttpClient({ baseUrl: BASE_URL });

type SearchNewsResult = {
  news: StrapiNewsItem[];
  total: number;
};

async function fetchNewsClient(
  params: FetchNewsParams,
  locale?: string,
): Promise<StrapiNewsItem[]> {
  // Auto-detect language from locale if not explicitly provided
  let language: string | undefined = params.language;

  if (!language && locale) {
    language = localeToStrapiLanguage(locale);
  }

  const dto = await http.get<StrapiNewsResponseDto>(`/api/news`, {
    query: {
      page: params.page,
      size: params.size,
      slug: params.slug,
      category: params.category,
      ...(params.tagLike ? { tagLike: encodeURIComponent(params.tagLike) } : {}),
      ...(params.authorName ? { authorName: encodeURIComponent(params.authorName) } : {}),
      ...(params.tagList ? { tagList: params.tagList } : {}),
      ...(params.getLatestTwoDays ? { getLatestTwoDays: params.getLatestTwoDays } : {}),
      ...(params.fromCreatedAt ? { fromCreatedAt: params.fromCreatedAt } : {}),
      ...(params.toCreatedAt ? { toCreatedAt: params.toCreatedAt } : {}),
      ...(params.titleLike ? { titleLike: encodeURIComponent(params.titleLike) } : {}),
      ...(params.contentLike ? { contentLike: encodeURIComponent(params.contentLike) } : {}),
      ...(params.notCategory ? { notCategory: params.notCategory } : {}),
      ...(params.tagNotLike ? { tagNotLike: encodeURIComponent(params.tagNotLike) } : {}),
      ...(params.notTagList ? { notTagList: params.notTagList } : {}),
      ...(language ? { language } : {}),
    },
  });
  return (dto.data ?? [])
    .filter((item): item is NonNullable<typeof item> => item != null)
    .map(mapStrapiNews);
}

async function searchNewsClient(
  params: FetchNewsParams,
  locale?: string,
): Promise<SearchNewsResult> {
  // Auto-detect language from locale if not explicitly provided
  let language: string | undefined = params.language;

  if (!language && locale) {
    language = localeToStrapiLanguage(locale);
  }

  const dto = await http.get<StrapiNewsResponseDto>(`/api/news`, {
    query: {
      page: params.page,
      size: params.size,
      slug: params.slug,
      category: params.category,
      ...(params.tagLike ? { tagLike: encodeURIComponent(params.tagLike) } : {}),
      ...(params.authorName ? { authorName: encodeURIComponent(params.authorName) } : {}),
      ...(params.tagList ? { tagList: params.tagList } : {}),
      ...(params.getLatestTwoDays ? { getLatestTwoDays: params.getLatestTwoDays } : {}),
      ...(params.fromCreatedAt ? { fromCreatedAt: params.fromCreatedAt } : {}),
      ...(params.toCreatedAt ? { toCreatedAt: params.toCreatedAt } : {}),
      ...(params.titleLike ? { titleLike: encodeURIComponent(params.titleLike) } : {}),
      ...(params.contentLike ? { contentLike: encodeURIComponent(params.contentLike) } : {}),
      ...(params.notCategory ? { notCategory: params.notCategory } : {}),
      ...(params.tagNotLike ? { tagNotLike: encodeURIComponent(params.tagNotLike) } : {}),
      ...(params.notTagList ? { notTagList: params.notTagList } : {}),
      ...(params.conditionOperator ? { conditionOperator: params.conditionOperator } : {}),
      ...(language ? { language } : {}),
    },
  });
  return {
    news: (dto.data ?? [])
      .filter((item): item is NonNullable<typeof item> => item != null)
      .map(mapStrapiNews),
    total: dto.meta?.pagination?.total ?? 0,
  };
}

export const useSearchNews = (searchQuery: string | null) => {
  const locale = useLocale();

  return useQuery<SearchNewsResult, Error>({
    queryKey: [STRAPI_SEARCH_NEWS_QK, searchQuery, locale],
    queryFn: async () => {
      if (!searchQuery || searchQuery.trim().length === 0) {
        return { news: [], total: 0 };
      }
      const trimmedQuery = searchQuery.trim();
      return searchNewsClient(
        {
          titleLike: trimmedQuery,
          contentLike: trimmedQuery,
          tagLike: trimmedQuery,
          conditionOperator: 'or',
          size: 5,
        },
        locale,
      );
    },
    enabled: Boolean(searchQuery && searchQuery.trim().length > 0),
    staleTime: 30_000, // 30 seconds
    refetchOnWindowFocus: false,
  });
};

export const useStrapiSections = (categorySlug: string | null) => {
  const locale = useLocale();

  return useQuery<StrapiCategoryItem[], Error>({
    queryKey: [STRAPI_SECTIONS_QK, categorySlug, locale],
    queryFn: async () => {
      if (!categorySlug) return [];
      return fetchStrapiSectionsByCategory(categorySlug, locale);
    },
    enabled: Boolean(categorySlug),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useStrapiTopStories = (categorySlug: string | null) => {
  return useQuery<StrapiNewsStoryItem[], Error>({
    queryKey: [STRAPI_TOP_STORIES_QK, categorySlug],
    queryFn: async () => {
      if (!categorySlug) return [];
      return fetchStrapiTopStoriesByCategory(categorySlug);
    },
    enabled: Boolean(categorySlug),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useHottestNews = () => {
  const locale = useLocale();

  return useQuery<StrapiNewsItem[], Error>({
    queryKey: [STRAPI_HOTTEST_NEWS_QK, locale],
    queryFn: async () => {
      return fetchNewsClient(
        {
          tagLike: 'hottest news',
          size: 3,
        },
        locale,
      );
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useSearchEventsProxy = (searchQuery: string | null, size = 6) => {
  const locale = useLocale();
  const trimmed = searchQuery?.trim() ?? '';

  return useQuery<SearchEventsProxyResult, Error>({
    queryKey: [STRAPI_SEARCH_EVENTS_QK, trimmed, locale, size],
    queryFn: () => searchEventsProxy({ q: trimmed, locale, size, page: 1 }),
    enabled: trimmed.length >= MIN_SEARCH_LENGTH,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
};

export const useTrendingEventsProxy = (limit = 3) => {
  const locale = useLocale();

  return useQuery<SearchEventsProxyResult, Error>({
    queryKey: [STRAPI_TRENDING_EVENTS_QK, locale, limit],
    queryFn: () =>
      searchEventsProxy({
        sortBy: 'VOLUME_24H',
        sortDirection: 'DESC',
        size: limit,
        page: 1,
        locale,
      } satisfies SearchEventsProxyParams),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useSearchProfilesProxy = (searchQuery: string | null, size = 6) => {
  const trimmed = searchQuery?.trim() ?? '';

  return useQuery<SearchProfilesProxyResult, Error>({
    queryKey: [STRAPI_SEARCH_PROFILES_QK, trimmed, size],
    queryFn: () => searchProfilesProxy({ q: trimmed, size, page: 1 }),
    enabled: trimmed.length >= MIN_SEARCH_LENGTH,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
};

export const useSearchNewsProxy = (searchQuery: string | null, size = 6) => {
  const locale = useLocale();
  const trimmed = searchQuery?.trim() ?? '';

  return useQuery<SearchNewsProxyResult, Error>({
    queryKey: [STRAPI_SEARCH_NEWS_PROXY_QK, trimmed, size, locale],
    queryFn: () => searchNewsProxy({ q: trimmed, size, page: 1, locale }),
    enabled: trimmed.length >= MIN_SEARCH_LENGTH,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
};

export const useTrendingNewsProxy = (limit = 3) => {
  const locale = useLocale();

  return useQuery<SearchNewsProxyResult, Error>({
    queryKey: [STRAPI_TRENDING_NEWS_PROXY_QK, limit, locale],
    queryFn: () => searchNewsProxy({ size: limit, page: 1, locale }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useTrendingProfilesProxy = (limit = 6) => {
  return useQuery<SearchProfilesProxyResult, Error>({
    queryKey: [STRAPI_TRENDING_PROFILES_QK, limit],
    queryFn: () => searchProfilesProxy({ size: limit, page: 1 }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Articles whose title holds the query, pictures included — the header search
 * (13.10) marks the match in the title, so a body-only match would show
 * nothing to mark. The previous answer stays up while the next one loads.
 */
export const useSearchNewsTitles = (searchQuery: string | null, size = 6) => {
  const locale = useLocale();
  const trimmed = searchQuery?.trim() ?? '';
  return useQuery<SearchNewsResult, Error>({
    queryKey: [STRAPI_SEARCH_NEWS_TITLES_QK, trimmed, size, locale],
    queryFn: () => searchNewsClient({ titleLike: trimmed, size }, locale),
    enabled: trimmed.length > 0,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
};

/** The newest articles in the reader's language, with pictures. */
export const useLatestNews = (size = 3) => {
  const locale = useLocale();
  return useQuery<NewsList, Error>({
    queryKey: [STRAPI_LATEST_NEWS_QK, size, locale],
    queryFn: () => fetchNewsList({ locale, size }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
};
